import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks } from 'lucide-react';
import type { ReactElement, ReactNode, SVGProps } from 'react';
import { useState } from 'react';

import type { FixListEntry, FixListStatus } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { dispatchFixAgent } from '../api/canvas-captures';
import { apiFetch } from '../api/client';
import { useLiveQuery } from '../api/use-live-query';
import {
  AlertIcon,
  CheckIcon,
  CloseIcon,
  CopyIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestIcon,
  LinkIcon,
} from '../lib/icons';

/**
 * The fix list, at the level it belongs to: the project.
 *
 * A finding is about the project, not about the session it happened to be
 * spotted in — someone reviewing two sessions of the same site is building one
 * list of things to fix. Every screen inside a project shows the same count.
 */

export const fixListKeys = {
  all: ['fix-list'] as const,
  project: (projectId: string) => [...fixListKeys.all, projectId] as const,
};

function listFixList(projectId: string): Promise<FixListEntry[]> {
  return apiFetch<FixListEntry[]>(
    `/fix-list?projectId=${encodeURIComponent(projectId)}`,
  );
}

export function useFixList(projectId?: string) {
  const key = fixListKeys.project(projectId ?? 'none');

  const query = useQuery({
    enabled: Boolean(projectId),
    queryFn: () => listFixList(projectId!),
    queryKey: key,
    // No polling: the stream below keeps this live, and it is a Firestore
    // watch, so every (re)connect starts with the whole list. A 2s poll beside
    // it was ~30% of all API requests (plus a CORS preflight for many).
  });

  /**
   * The same cache entry, kept live.
   *
   * A coding agent resolving issues over MCP is the case this exists for: the
   * work happens in someone's editor, and the count in the top bar used to sit
   * at whatever it was until the tab was reloaded or refocused. The fetch above
   * still does the first paint, so a stream that never connects — a proxy that
   * buffers, an offline tab — leaves the list exactly as it behaved before.
   */
  useLiveQuery<FixListEntry[]>({
    enabled: Boolean(projectId),
    path: `/fix-list/stream?projectId=${encodeURIComponent(projectId ?? '')}`,
    queryKey: key,
  });

  const entries = query.data ?? [];

  return {
    entries,
    /** Only what's still to do — dismissed findings stay recorded, not counted. */
    openCount: entries.filter((entry) => entry.status === 'fix').length,
  };
}

/**
 * A project's fix list from the cache, without a stream or a poll of its
 * own — for the pieces of a board that read the list `useFixList` already
 * keeps live, so each does not open another stream beside it.
 */
export function useFixListEntries(
  projectId?: string,
): FixListEntry[] | undefined {
  const { data } = useQuery({
    enabled: Boolean(projectId),
    queryFn: () => listFixList(projectId!),
    queryKey: fixListKeys.project(projectId ?? 'none'),
  });
  // Undefined until loaded, so a reader can tell "none yet" from "empty".
  return data;
}

export const STATUS_LABEL: Record<FixListStatus, string> = {
  fix: 'To fix',
  fixed: 'Fixed',
  not_an_issue: 'Not an issue',
  wont_fix: "Won't fix",
};

/** Three visual states, not four: outstanding, done, and dismissed. 'fixed' is
 *  deliberately not struck through — it is the one resolution that represents
 *  work having happened, and reading it as crossed-off-and-forgotten is the
 *  opposite of what it means. */
const STATUS_STYLE: Record<FixListStatus, string> = {
  fix: 'font-medium text-text-secondary',
  fixed: 'font-semibold text-success-ink',
  not_an_issue: 'text-text-muted line-through',
  wont_fix: 'text-text-muted line-through',
};

/** Outstanding first, then done, then dismissed. */
export const STATUS_ORDER: Record<FixListStatus, number> = {
  fix: 0,
  fixed: 1,
  not_an_issue: 2,
  wont_fix: 2,
};

/** The pill in the top bar. Shows what is still outstanding, not the total. */
export function FixListButton({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      aria-label={`Fix list, ${String(count)} open`}
      className="flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-card bg-critical pr-1.5 pl-3.5 text-ui font-semibold whitespace-nowrap text-white transition duration-200 hover:brightness-95 active:scale-[0.98]"
      onClick={onClick}
      type="button"
    >
      <ListChecks aria-hidden="true" className="size-4" />
      <span>Fix list</span>
      {/* The count is the point of this button; the words are context for it. */}
      <span className="flex h-7 min-w-7 items-center justify-center rounded-button bg-surface px-1.5 text-ui font-bold text-text-primary tabular-nums">
        {count}
      </span>
    </button>
  );
}

/** Severity, as ink on the card rather than a filled chip — the card's own
 *  border already carries the emphasis once it is selected. */
const SEVERITY_INK: Record<string, string> = {
  Critical: 'text-critical',
  Major: 'text-tag-major-ink',
  Minor: 'text-text-muted',
  Suggestion: 'text-text-muted',
};

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'flex size-[22px] shrink-0 items-center justify-center rounded-sm transition duration-200',
        checked
          ? 'bg-ink text-white'
          : 'bg-surface ring-1 ring-inset ring-hairline-alt',
      ].join(' ')}
    >
      {checked ? <CheckIcon className="size-3.5" /> : null}
    </span>
  );
}

/** Everything that differs between a PR's three outcomes — icon, label, and
 *  colour — in one place, so `PrCardRow` picks a row of this rather than
 *  branching on `entry.prStatus` three separate times for icon, badge, and
 *  background. Each icon is GitHub's own octicon for that exact state, not
 *  one shape recoloured, and each colour is GitHub's own (not `success`/
 *  `critical` — see the tokens' own comment in tailwind.config.ts).
 *
 *  Every class name here is written out in full, never assembled from a
 *  shared fragment (`` `bg-${ink}/10` ``) — Tailwind's build scans source
 *  text for literal class strings, so an interpolated one is invisible to it
 *  and the CSS for that class is simply never generated. */
const PR_STATE: Record<
  'opened' | 'merged' | 'closed',
  {
    Icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
    label: string;
    iconClass: string;
  }
> = {
  closed: {
    Icon: GitPullRequestClosedIcon,
    iconClass: 'text-pr-closed',
    label: 'Closed',
  },
  merged: {
    Icon: GitMergeIcon,
    iconClass: 'text-pr-merged',
    label: 'Merged',
  },
  opened: {
    Icon: GitPullRequestIcon,
    iconClass: 'text-pr-open',
    label: 'Open',
  },
};

/** The coding agent's own progress on one entry — pending, failed, or the PR
 *  it opened (open, merged, or closed — the last two arriving later, over
 *  `github-webhook.controller.ts`, not from this app's own decision). Set by
 *  `PrAgentService.dispatch` in the background (fired in bulk from the
 *  panel's "Fix with Floto Agent", not per entry) and arriving here through
 *  the panel's existing live stream with no polling of its own.
 *
 *  A run in progress says one thing and keeps saying it. It used to print
 *  `entry.prStep` — the agent's live tool calls, "Reading src/App.tsx",
 *  "Listing the repo" — which turned a card into a scrolling log of somebody
 *  else's repo internals: text that changed every few seconds, that nobody
 *  can act on, and that reads as the product exposing its plumbing rather
 *  than doing the work. The line the reporter actually needs is that it is
 *  running, and the outcome when it lands.
 *
 *  A sibling of the card's selectable button, not a child of it — an `<a>`
 *  nested inside that `<button>` would be invalid HTML and an unreliable
 *  click target, the same reason project-card.tsx's overflow menu sits beside
 *  its Link rather than inside it. `FixListEntryCard` gives the two a shared
 *  border/background so they read as one card with a divider, the way a
 *  project-management tool like Linear surfaces a linked PR beneath an
 *  issue rather than as a bare, separately-styled link. */
export function PrCardRow({ entry }: { entry: FixListEntry }) {
  if (entry.prStatus === 'pending') {
    return (
      <div className="flex items-center gap-2 border-t border-hairline px-4 py-3.5 text-xs">
        <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-text-muted" />
        {/* Same shimmer as the bottom-centre notice, deliberately — one
            "working" treatment across both surfaces, so the card and the
            pill are recognisably the same run. */}
        <span className="animate-text-shimmer bg-gradient-to-r from-text-muted via-text-primary to-text-muted bg-[length:200%_100%] bg-clip-text text-transparent motion-reduce:animate-none motion-reduce:bg-none motion-reduce:text-text-muted">
          Floto Agent is working…
        </span>
      </div>
    );
  }

  if (entry.prStatus === 'failed') {
    return (
      <div className="flex items-center gap-1.5 border-t border-hairline px-4 py-3.5 text-xs font-medium text-critical">
        <AlertIcon className="size-3.5 shrink-0" />
        {entry.prError ?? 'Could not open a pull request'}
      </div>
    );
  }

  const status = entry.prStatus;
  if (
    (status === 'opened' || status === 'merged' || status === 'closed') &&
    entry.prUrl
  ) {
    const { Icon, iconClass, label } = PR_STATE[status];
    const meta = [
      entry.prNumber ? `#${String(entry.prNumber)}` : null,
      entry.prBranch,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <a
        aria-label={`${entry.prTitle ?? 'Pull request'} — ${label}`}
        className="flex items-center gap-3 border-t border-hairline px-4 py-3.5 transition hover:bg-warm"
        href={entry.prUrl}
        rel="noreferrer"
        target="_blank"
      >
        {/* No separate status badge: the icon's own colour (green/purple/red,
            matching GitHub's own convention) already says open/merged/closed
            — a text pill beside it repeated the same information. The `title`
            (on this wrapping span, not the `aria-hidden` icon itself, which
            doesn't accept one) gives a sighted mouse hover the same word;
            `aria-label` on the link is what actually reaches assistive tech. */}
        <span className="shrink-0" title={label}>
          <Icon className={`size-4 ${iconClass}`} />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block truncate text-ui font-semibold text-text-primary">
            {entry.prTitle ?? 'Pull request'}
          </span>
          {meta ? (
            <p className="mt-0.5 truncate text-xs text-text-muted">{meta}</p>
          ) : null}
        </div>
        <LinkIcon className="size-4 shrink-0 text-text-muted" />
      </a>
    );
  }

  return null;
}

/** One entry: pick it or don't, and read why it is here.
 *
 *  Only an outstanding finding is selectable — a resolved one is kept for the
 *  record, and offering to hand an agent something already fixed is how the
 *  same change gets made twice. */
export function FixListEntryCard({
  actions,
  badge,
  entry,
  onToggle,
  selected,
}: {
  /** Controls under the card, outside its selectable button — a button
   *  cannot hold another. */
  actions?: ReactNode;
  /** A word beside the severity — "New", "Updated". */
  badge?: string;
  entry: FixListEntry;
  onToggle?: () => void;
  selected: boolean;
}) {
  const selectable = entry.status === 'fix';
  const recommendation = entry.issue.recommendation?.trim() ?? '';
  const hasRecommendation =
    recommendation.length > 0 &&
    recommendation !== entry.issue.description.trim();

  const body = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2.5">
          {selectable ? <Checkbox checked={selected} /> : null}
          <span
            className={`text-tag font-bold tracking-[0.06em] uppercase ${SEVERITY_INK[entry.issue.severity] ?? ''}`}
          >
            {entry.issue.severity}
          </span>
          {badge ? (
            <span className="rounded-pill bg-idea-soft px-1.5 py-0.5 text-badge font-semibold text-text-primary duration-300 animate-in fade-in motion-reduce:animate-none">
              {badge}
            </span>
          ) : null}
        </span>
        <span
          className={[
            'flex items-center gap-1 text-tag',
            selectable ? 'text-text-muted' : STATUS_STYLE[entry.status],
          ].join(' ')}
        >
          {entry.status === 'fixed' ? <CheckIcon className="size-3" /> : null}
          {selectable ? entry.issue.category : STATUS_LABEL[entry.status]}
        </span>
      </div>

      {/* The finding is the heading and where it was found is the line
          under it: a card titled by its location ("Header nav",
          "stripe.com") says nothing about what is wrong. This card was rendering the description alone, so the
          extension picker's "What is it" answer — stored as
          `locationName` — was captured, sent to the coding agent, and then
          shown nowhere. 'Page-level' is the picker's own placeholder for
          "didn't say", so it is not worth a line. */}
      <p className="mt-3 text-subtitle leading-snug font-semibold text-text-primary">
        {entry.issue.description}
      </p>
      {entry.issue.locationName && entry.issue.locationName !== 'Page-level' ? (
        <p className="mt-1.5 truncate text-xs text-text-muted">
          On “{entry.issue.locationName}”
        </p>
      ) : null}

      {/* The area the reporter drew a box around, directly under the claim
          it is evidence for. Only ever present when somebody deliberately
          attached one — this is not the old behaviour where every manual
          report carried a redundant full-page screenshot, which is why it
          earns a place on the card where that one did not. The URL is
          signed per read by `FixListService`; the row stores only a path. */}
      {entry.issue.regionShotUrl ? (
        <img
          alt=""
          className="mt-3 w-full rounded-button border border-hairline bg-warm object-cover"
          loading="lazy"
          src={entry.issue.regionShotUrl}
        />
      ) : null}

      {/* A manual report has no recommendation of its own — the picker asks
          what is wrong and nothing else — so this block is skipped rather
          than printing the description a second time under a second
          heading. The equality check, not just the emptiness one, is what
          covers rows written while the writers still copied the
          description across (see `reportIssue` in the extension). */}
      {hasRecommendation ? (
        <div className="mt-3 rounded-button bg-warm px-3.5 py-3">
          <p className="text-badge font-semibold tracking-[0.08em] text-text-muted uppercase">
            Recommendation
          </p>
          <p className="mt-1 text-ui leading-relaxed font-medium text-text-primary">
            {entry.issue.recommendation}
          </p>
        </div>
      ) : null}

      {/* Who said it, in their words — on a fix the feedback room wrote off
          its own conversation. Each quote was checked against the answer it
          cites before it was stored. */}
      {entry.issue.evidence && entry.issue.evidence.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {entry.issue.evidence.map((said) => (
            <li
              className="border-l-2 border-hairline-alt pl-2.5 text-caption leading-snug text-text-secondary"
              key={`${said.personaId}:${said.messageId}`}
            >
              “{said.quote}”{' '}
              <span className="text-text-muted">
                — {said.personaName.split(' ')[0]}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );

  // The outer card and the inner button are separate elements so `PrCardRow`
  // (an `<a>` for an opened PR) can sit beside the button rather than inside
  // it, while still reading as one bordered card with a divider between the
  // two — not two stacked, separately-styled boxes.
  const card = [
    'overflow-hidden rounded-card bg-surface transition duration-200',
    selected
      ? 'ring-2 ring-brand/55'
      : 'ring-1 ring-hairline hover:ring-hairline-alt',
  ].join(' ');

  const inner = [
    'block w-full p-4 text-left',
    selectable ? 'cursor-pointer' : 'opacity-70',
  ].join(' ');

  return (
    <li>
      <div className={card}>
        {selectable ? (
          <button
            aria-pressed={selected}
            className={inner}
            onClick={onToggle}
            type="button"
          >
            {body}
          </button>
        ) : (
          <div className={inner}>{body}</div>
        )}
        <PrCardRow entry={entry} />
        {actions}
      </div>
    </li>
  );
}

/** The page the fixes were found on, when the caller knows it — what lets an
 *  agent tell which route or template to open first. */
export interface PromptPage {
  url?: string;
  name?: string;
}

/** One prompt is the fix list's job to hand an agent, not one per issue.
 *
 *  Plain findings only — no MCP instructions. The `floto` MCP server
 *  (apps/mcp-sse) is not part of the v1 launch, so a prompt telling the agent
 *  to call get_fix_list / resolve_issue would dead-end for every user.
 *
 *  A finding with no agentPrompt is written from the issue itself rather than
 *  skipped. The prompt is generated by a Gemini call when the entry is added
 *  (fix-list-enrichment.service.ts), and nothing backfills it — so every entry
 *  recorded before GEMINI3_API_KEY was configured has none, permanently.
 *  Requiring it hid this button on five of six real apps: the user marks
 *  findings Fix, opens the list, and the one action it offers is not there. The
 *  issue's own description and recommendation are always present and are the
 *  actual content; the generated prompt only phrases them for an agent. */
export function buildCombinedPrompt(
  toFix: FixListEntry[],
  page: PromptPage = {},
): string {
  if (toFix.length === 0) return '';

  const n = toFix.length;
  const fromRoom = toFix.some((entry) => entry.issue.evidence?.length);
  const onPage = [
    page.url ? `Page: ${page.url}` : '',
    page.name ? `Screen: ${page.name}` : '',
  ].filter(Boolean);

  const header = [
    n === 1
      ? 'Make one change to this codebase.'
      : `Make ${String(n)} changes to this codebase, one per section below.`,
    fromRoom
      ? `${n === 1 ? 'It comes' : 'Each comes'} from a feedback session where simulated users (AI personas) looked at the live site and said what got in their way. Their words are quoted under ${n === 1 ? 'the change' : 'each change'}: it is done when it answers them.`
      : `${n === 1 ? 'It comes' : 'Each comes'} from a review of the live site.`,
    ...(onPage.length ? ['', ...onPage] : []),
    '',
    'How to work:',
    '- Locations name what a visitor sees on the page, not a file. Find the component by its visible text or role before editing anything.',
    '- Make the change asked for, in that place, and nothing else — no redesigns, no unrelated clean-up.',
    "- If you can't find it in this codebase, or the change doesn't apply, say so instead of changing something similar.",
    ...(fromRoom
      ? [
          "- Never invent facts. Numbers, prices, percentages, timeframes or terms in a change or a quote are what a simulated user asked for, not the business's real figures. Use real values already in this codebase; where there are none, put a clearly marked placeholder (e.g. `TODO: real reimbursement %`) and list it in your summary.",
        ]
      : []),
    '- When you finish, list each change as done or not done, with the files you touched. Never report one as done that you did not change.',
  ].join('\n');

  const findings = toFix
    .map((entry, index) => {
      const { issue } = entry;
      const title =
        n === 1 ? '## The change' : `## Change ${String(index + 1)}`;
      // A generated prompt, where one was made, already says all of this in
      // its own words.
      if (entry.agentPrompt) {
        return `${title}: ${issue.description}\n\n${entry.agentPrompt}`;
      }
      const named = issue.locationName.trim().replace(/[.:;,]+$/, '');
      const where =
        named && named !== 'Page-level' ? named : 'the page as a whole';
      // Their own quote marks would double up with ours.
      const quotes = (issue.evidence ?? []).map(
        (said) =>
          `- ${said.personaName}: "${said.quote
            .trim()
            .replace(/^["“”]+|["“”]+$/g, '')
            .replace(/"/g, "'")}"`,
      );
      const weight = [
        issue.impact ? `impact ${issue.impact}` : '',
        issue.effort ? `effort ${issue.effort}` : '',
      ].filter(Boolean);
      return [
        `${title}: ${issue.recommendation}`,
        '',
        `Where: ${where}`,
        `Problem: ${issue.severity === 'Critical' ? 'Blocker. ' : ''}${issue.description}`,
        ...(weight.length
          ? [`Severity: ${issue.severity} (${weight.join(', ')})`]
          : [`Severity: ${issue.severity}`]),
        ...(quotes.length ? ['', 'What people said:', ...quotes] : []),
      ].join('\n');
    })
    .join('\n\n');

  return `${header}\n\n${findings}`;
}

/** The panel's action bar: what is selected, and the two things to do with it.
 *
 *  "Fix with Floto Agent" runs Floto's own coding agent (`PrAgentService`,
 *  via the same `dispatchFixAgent` call the PR row reads the result of) on
 *  **exactly one** entry. Not a bulk action: each run is a branch, a commit
 *  and a pull request, so five selected findings is five PRs against the
 *  same repo opened at once, each unaware of the others' edits, and a
 *  reviewer left to work out which branch to take first. One fix, one PR.
 *  The button therefore stays disabled for a multi-selection and says why.
 *
 *  "Copy prompt" is the older, separate path and deliberately keeps taking
 *  the whole selection: it hands the findings to somebody's own coding agent,
 *  which decides for itself how to sequence them. */
export function FixListFooter({ selected }: { selected: FixListEntry[] }) {
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const combined = buildCombinedPrompt(selected);
  const copyDisabled = !combined;

  /** The one entry the agent can run on, or undefined plus the reason why
   *  not — a disabled button that explains nothing is exactly what made this
   *  control read as broken before. */
  const [target, blocked] = ((): [FixListEntry | undefined, string] => {
    if (selected.length > 1) {
      return [undefined, 'The agent takes one fix at a time — select just one'];
    }
    const only = selected[0];
    if (!only) return [undefined, 'Select a fix to start the agent'];
    if (only.prStatus) {
      return [undefined, 'The agent has already run on this one'];
    }
    return [only, ''];
  })();

  const dispatchMutation = useMutation({
    mutationFn: () => {
      if (!target) throw new Error('No fix selected');
      return dispatchFixAgent(target.id);
    },
    // Invalidate and nothing else. The row the user just acted on is in front
    // of them and the live stream flips it to "Floto Agent is working…" on its
    // own — there is no second surface left to notify.
    onSuccess: (entry) => {
      void queryClient.invalidateQueries({
        queryKey: fixListKeys.project(entry.projectId),
      });
    },
  });

  const dispatchDisabled = !target || dispatchMutation.isPending;

  /**
   * A refused dispatch has to say why, and until now none of them could.
   *
   * `POST /fix-list/:id/dispatch` answers 400 when the project has no GitHub repo
   * connected — the commonest case there is, since a newly created project has
   * none — and with no `onError` here that rejection went nowhere: the button
   * un-disabled itself and the panel looked exactly as it does on success.
   * `ApiError` carries the server's own sentence, which already names the
   * fix ("Connect a GitHub repo to this app…"), so it is shown verbatim
   * rather than mapped to something vaguer.
   */
  const dispatchError = dispatchMutation.error;

  return (
    <div className="shrink-0 border-t border-hairline bg-surface px-5 py-4">
      {dispatchError ? (
        <p
          className="mb-3 rounded-button bg-tag-critical px-3.5 py-2.5 text-xs leading-snug text-critical duration-200 animate-in fade-in"
          role="alert"
        >
          {dispatchError instanceof Error
            ? dispatchError.message
            : 'Could not start the agent'}
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-4">
        <p className="text-caption text-text-secondary">
          <b className="font-semibold text-text-primary">{selected.length}</b>{' '}
          fix{selected.length === 1 ? '' : 'es'} selected
        </p>

        <div className="flex shrink-0 items-center gap-2">
          {/* Icon-only, with the label as a native tooltip — same height and
              corner radius as the primary action beside it, just narrower. */}
          <button
            aria-label={copied ? 'Prompt copied' : 'Copy prompt'}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-button text-text-muted ring-1 ring-hairline transition not-disabled:cursor-pointer hover:not-disabled:bg-warm hover:not-disabled:text-text-primary disabled:opacity-40"
            disabled={copyDisabled}
            onClick={() => {
              // A copy is the last thing this product observes before the
              // work moves to an editor.
              trackEvent('fix_list_prompt_copied', { count: selected.length });
              void navigator.clipboard.writeText(combined).then(() => {
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 2000);
              });
            }}
            title={copied ? 'Prompt copied' : 'Copy prompt'}
            type="button"
          >
            {copied ? (
              <CheckIcon className="size-4" />
            ) : (
              <CopyIcon className="size-4" />
            )}
          </button>

          <button
            className="flex h-10 shrink-0 items-center gap-2 rounded-button bg-ink px-5 text-ui font-semibold text-white transition duration-200 not-disabled:cursor-pointer hover:not-disabled:bg-ink/90 active:not-disabled:scale-[0.98] disabled:opacity-35"
            disabled={dispatchDisabled}
            onClick={() => {
              dispatchMutation.mutate();
            }}
            title={blocked || undefined}
            type="button"
          >
            {dispatchMutation.isPending ? 'Starting…' : 'Fix with Floto Agent'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function FixListPanel({
  entries,
  onClose,
}: {
  entries: FixListEntry[];
  onClose: () => void;
}) {
  // To-fix first: the list exists to be worked through, and a resolved finding
  // is kept for the record rather than for attention.
  const ordered = [...entries].sort(
    (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
  );

  const openIds = entries
    .filter((entry) => entry.status === 'fix')
    .map((entry) => entry.id);

  /**
   * Selected ids, and nothing is selected to begin with.
   *
   * This used to track the *exclusions*, because everything outstanding
   * started ticked — which made opening the panel an act with consequences:
   * the agent button was armed against the whole list, and "Copy prompt"
   * silently carried every finding whether or not the reader had looked at
   * one. Choosing is the user's, so the list opens with nothing chosen.
   *
   * The set has to hold the selection rather than the exclusions once the
   * default is empty: an exclusion set would have to be pre-filled with
   * every open id, and every finding arriving later — the fix list is a live
   * stream — would appear already ticked.
   */
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const selected = entries.filter(
    (entry) => entry.status === 'fix' && selectedIds.has(entry.id),
  );

  return (
    <aside className="flex w-[420px] shrink-0 flex-col border-l border-hairline bg-warm-alt duration-200 animate-in slide-in-from-right-4 motion-reduce:animate-none">
      <header className="shrink-0 border-b border-hairline bg-surface px-6 pt-5 pb-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-small-label text-text-muted uppercase">
              Your next steps
            </p>
            <h2 className="!mt-1 !mb-0 !text-panel-title !font-semibold !text-text-primary">
              Fix list
            </h2>
          </div>
          <button
            aria-label="Close fix list"
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-button text-text-secondary ring-1 ring-hairline transition hover:bg-warm hover:text-text-primary"
            onClick={onClose}
            type="button"
          >
            <CloseIcon className="size-4" />
          </button>
        </div>
        <p className="mt-2 text-ui leading-relaxed text-text-secondary">
          Improvements you&apos;ve chosen to make, across every session.
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
        {ordered.length === 0 ? (
          <p className="px-2 py-10 text-center text-ui text-text-secondary">
            Nothing here yet. Fixes from your feedback sessions collect on this
            list as people talk.
          </p>
        ) : (
          <ul className="space-y-3">
            {ordered.map((entry) => (
              <FixListEntryCard
                entry={entry}
                key={entry.id}
                onToggle={() => {
                  setSelectedIds((current) => {
                    const next = new Set(current);
                    if (next.has(entry.id)) next.delete(entry.id);
                    else next.add(entry.id);
                    return next;
                  });
                }}
                selected={selectedIds.has(entry.id)}
              />
            ))}
          </ul>
        )}
      </div>

      {openIds.length > 0 ? <FixListFooter selected={selected} /> : null}
    </aside>
  );
}

/**
 * The panel as a dismissable overlay, with no trigger of its own.
 *
 * Its own export because the panel has two callers that open it differently:
 * `FixList` below pairs it with a count pill, and the dashboard opens it
 * straight from a `?fix=<projectId>` link — the route the extension hands back
 * after filing a report. Before this existed the panel only rendered inside
 * a board, so a project whose entries all came from the extension (no session, no
 * capture, nothing to open) had a fix list with no way to reach it.
 */
export function FixListOverlay({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { entries } = useFixList(projectId);

  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-ink/10 duration-200 animate-in fade-in">
      <button
        aria-label="Close fix list"
        className="flex-1 cursor-default"
        onClick={onClose}
        type="button"
      />
      <FixListPanel entries={entries} onClose={onClose} />
    </div>
  );
}

/** Button + panel together, for a screen that just wants "the fix list". */
export function FixList({ projectId }: { projectId?: string }) {
  const { openCount } = useFixList(projectId);
  const [open, setOpen] = useState(false);

  if (!projectId) return null;

  return (
    <>
      <FixListButton
        count={openCount}
        onClick={() => {
          setOpen(true);
        }}
      />
      {open ? (
        <FixListOverlay
          projectId={projectId}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
